/**
 * lib/product-images/amazon-image-licence.ts — which image addresses are Amazon's own, and the licence rule
 * that follows from it (FRIDGE-1666).
 *
 * THE RULE. The Amazon Associates Program IP License (docs/catalog/asset-source-permissions.csv, the Amazon
 * rows, read 10/18/2026 from https://affiliate-program.amazon.com/help/operating/policies) allows an accepted
 * Associate to display Amazon's product images from Amazon's own image addresses, each linked to the matching
 * Amazon page, with a stored image link refreshed within 24 hours. It states: "You will not store or cache
 * Product Advertising Content consisting of an image". So Fridge.com never copies an Amazon image into its own
 * storage. The owner's 09/07/2026 decision on FRIDGE-386 says the same in general form: where redistribution is
 * not permitted, keep the official source link and track the exception.
 *
 * WHAT THIS MODULE IS. One predicate, `isAmazonImageUrl`, shared by every writer that could copy an image (the
 * mirror pipeline in lib/product-images/mirror.ts, which the ingest drain and the backfill routine run, and the
 * canonical-row mirror script). tests/amazon-image-licence-fridge-1666.test.ts pins it to the permission record:
 * every host the record attributes to Amazon matches, and no other recorded host does.
 *
 * It matches by HOST, parsed, never by substring: `m.media-amazon.com.example.net` is not Amazon's.
 *
 * STEP 2 (below). `amazonOriginImages` names the Amazon images in a product row, stored copies included, and the
 * one image resolver (lib/image-url.ts) leaves them out of every surface. The Amazon offer refresher
 * (lib/routines/commerce/amazon-offer-maintainer.ts) stores Amazon's own current image link for a product that has
 * no other photo, renews it inside the licence's 24 hours and deletes it before they end; the product page shows
 * it only while current, linked to the Amazon offer (app/api/amazon-photo, components/product/amazon-photo-link).
 */

/** Amazon's image hosts, exactly (the hosts the permission record names, plus Amazon's older image hosts). */
const AMAZON_IMAGE_HOSTS = new Set([
  'm.media-amazon.com',
  'images-na.ssl-images-amazon.com',
  'images-eu.ssl-images-amazon.com',
  'images-fe.ssl-images-amazon.com',
  'ecx.images-amazon.com',
  'g-ecx.images-amazon.com',
])

/** Amazon's image host families: any subdomain of these is Amazon's image delivery network. */
const AMAZON_IMAGE_HOST_SUFFIXES = ['.media-amazon.com', '.ssl-images-amazon.com', '.images-amazon.com']

/** True when `host` (a bare hostname) is one of Amazon's image hosts. */
export function isAmazonImageHost(host: string | null | undefined): boolean {
  if (!host) return false
  const h = host.trim().toLowerCase().replace(/\.$/, '')
  if (AMAZON_IMAGE_HOSTS.has(h)) return true
  return AMAZON_IMAGE_HOST_SUFFIXES.some((suffix) => h.endsWith(suffix))
}

/**
 * True when `url` is an absolute http(s) address on one of Amazon's image hosts. A relative path, a Fridge.com
 * storage address or an unparseable string is not.
 */
export function isAmazonImageUrl(url: string | null | undefined): boolean {
  if (typeof url !== 'string' || !url.trim()) return false
  let parsed: URL
  try {
    parsed = new URL(url.trim())
  } catch {
    return false
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false
  return isAmazonImageHost(parsed.hostname)
}

/** The per-image refusal reason the mirror records for an Amazon image. */
export const AMAZON_IMAGE_NOT_STORED = 'amazon_licence_no_stored_copy'

// ---------------------------------------------------------------------------------------------------------------
// Step 2: no page shows a stored copy or a stored Amazon address; the product page may show Amazon's own image
// link, current and linked to the Amazon page.
//
// The licence's words (IP License §2(h) and §2(c)(iii), read 10/26/2026): "You will not store or cache Product
// Advertising Content consisting of an image", but "you may store a link to Product Advertising Content consisting
// of an image for up to 24 hours", and "You will link each use of Product Advertising Content to, and only to, the
// relevant page of an Amazon Site".
// ---------------------------------------------------------------------------------------------------------------

/** Non-empty string entries, untrimmed: the list the mirror numbers its copies from (mirror.ts). */
const nonEmptyEntries = (list: unknown): string[] =>
  Array.isArray(list) ? list.filter((u): u is string => typeof u === 'string' && u.length > 0) : []

/** A copy in the product-images bucket: an absolute storage address or its site-relative /product-images/ form. */
const isProductImageCopy = (url: string) => /(^|\/)product-images\//.test(url.split(/[?#]/)[0])

/**
 * The stored entry a mirrored copy was made from. The mirror names each copy by the 1-based position of its source
 * among the row's non-empty `images` entries (`fridge-com-<brand>-<model>-<n>.<ext>`), so the number in the file
 * name finds the source; a name without one falls back to the copy's own position. The FRIDGE-386 served-host
 * receipt and the Step 1 receipt attribute copies by the same rule.
 */
export function mirrorSourceUrl(mirrorUrl: string, raw: readonly string[], mirrored: readonly string[]): string | null {
  const named = /-(\d+)\.[a-z0-9]+$/i.exec(mirrorUrl.split(/[?#]/)[0])
  const position = named ? Number(named[1]) : mirrored.indexOf(mirrorUrl) + 1
  return position >= 1 ? raw[position - 1] ?? null : null
}

/**
 * Every image in a product row that is Amazon's: an address on one of Amazon's image hosts, in either column, and
 * every stored copy made from one. Trimmed, the form lib/image-url.ts compares. The one image resolver
 * (resolveCanonicalImages) leaves these out, so no card, rail, gallery, schema block, sitemap or share image shows
 * a stored copy of an Amazon image or a stored Amazon address.
 *
 * A copy whose position names no recorded source (the row's `images` was shortened after the copy was made) counts
 * as Amazon's when every source the row records is Amazon's: the mirror copies only the row's own sources, and this
 * row has recorded no other. Measured 10/26/2026: 64 live rows, 202 such copies. On a row with any other recorded
 * source, such a copy is kept.
 */
export function amazonOriginImages(row: { images?: unknown; images_supabase?: unknown } | null | undefined): Set<string> {
  const out = new Set<string>()
  if (!row) return out
  const raw = nonEmptyEntries(row.images)
  const mirrored = nonEmptyEntries(row.images_supabase)
  for (const url of raw) if (isAmazonImageUrl(url)) out.add(url.trim())
  const onlyAmazonSources = raw.length > 0 && raw.every((url) => isAmazonImageUrl(url))
  for (const url of mirrored) {
    if (isAmazonImageUrl(url)) { out.add(url.trim()); continue }
    if (!isProductImageCopy(url)) continue
    const source = mirrorSourceUrl(url, raw, mirrored)
    if (source ? isAmazonImageUrl(source) : onlyAmazonSources) out.add(url.trim())
  }
  return out
}

/** The product_source_data slot that holds a product's current Amazon image link: one row per FIN, no migration. */
export const AMAZON_IMAGE_LINK_SOURCE = 'amazon_image_link'
/** The licence's limit on a stored image link. A link this old is never shown. */
export const AMAZON_IMAGE_LINK_MAX_AGE_MS = 24 * 60 * 60 * 1000
/** The refresher reads a link again once it is this old, so a shown link is renewed about twice a day. */
export const AMAZON_IMAGE_LINK_REFRESH_AFTER_MS = 12 * 60 * 60 * 1000
/**
 * At the end of every run the refresher deletes a link this old that it did not renew. Its runs are six hours
 * apart, so with this bound no stored link outlives the licence's 24 hours.
 */
export const AMAZON_IMAGE_LINK_PURGE_AFTER_MS = 18 * 60 * 60 * 1000

/** A type, not an interface, so it is assignable to the database's JSON column type. */
export type AmazonImageLink = {
  /** Amazon's own image address, exactly as Amazon's API returned it. */
  url: string
  width: number | null
  height: number | null
  /** The item Amazon returned it for. The image links to that item's Amazon page and no other. */
  asin: string
}

const ASIN = /^[A-Z0-9]{10}$/

/**
 * Amazon's primary large image for one item of a GetItems answer, in either API's shape (Creators API camelCase,
 * PA-API PascalCase). Only an https address on an Amazon image host counts; anything else is no image.
 */
export function primaryImageOfItem(item: unknown): AmazonImageLink | null {
  const it = item as Record<string, any> | null
  const asin = String(it?.asin ?? it?.ASIN ?? '').toUpperCase()
  const large = it?.images?.primary?.large ?? it?.Images?.Primary?.Large
  const url = typeof (large?.url ?? large?.URL) === 'string' ? String(large.url ?? large.URL).trim() : ''
  if (!ASIN.test(asin) || !/^https:\/\//i.test(url) || !isAmazonImageUrl(url)) return null
  const size = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.round(v) : null)
  return { url, width: size(large.width ?? large.Width), height: size(large.height ?? large.Height), asin }
}

/** The ASIN an Amazon product page address names (`/dp/<ASIN>` or `/gp/product/<ASIN>`); null when it names none. */
export function asinOfAmazonPageUrl(url: string | null | undefined): string | null {
  if (typeof url !== 'string') return null
  const m = /\/(?:dp|gp\/product)\/([A-Z0-9]{10})(?=[/?#]|$)/i.exec(url)
  return m ? m[1].toUpperCase() : null
}

/**
 * The product_source_data row the refresher writes for one FIN. `extracted.images` stays empty, the same rule as
 * the official-link exception, so the gallery resolver never takes the link for a photo of ours; `url` is the
 * Amazon page the image links to.
 */
export function amazonImageLinkRow(fin: string, link: AmazonImageLink, detailPageUrl: string, fetchedAt: string) {
  return {
    fin,
    source: AMAZON_IMAGE_LINK_SOURCE,
    url: detailPageUrl,
    fetched_at: fetchedAt,
    extracted: { images: [] as string[], amazon_image: link },
    quality: { image_count: 0, link_only: true },
    raw: null,
  }
}

/**
 * The image link a stored row holds, while it may still be shown: the row is in the slot, it names an ASIN, its
 * address is https on an Amazon image host, and it was written less than 24 hours ago. Null otherwise.
 * `expiresAt` is when the licence's 24 hours end; a response that carries the link must not be cached past it.
 */
export function currentAmazonImageLink(
  row: { source?: unknown; fetched_at?: unknown; extracted?: unknown } | null | undefined,
  now: number = Date.now(),
): (AmazonImageLink & { expiresAt: number }) | null {
  if (!row || row.source !== AMAZON_IMAGE_LINK_SOURCE) return null
  const fetched = typeof row.fetched_at === 'string' ? Date.parse(row.fetched_at) : NaN
  if (!Number.isFinite(fetched) || fetched > now + 5 * 60_000) return null
  const expiresAt = fetched + AMAZON_IMAGE_LINK_MAX_AGE_MS
  if (expiresAt <= now) return null
  const link = (row.extracted as Record<string, any> | null)?.amazon_image
  const asin = typeof link?.asin === 'string' ? link.asin.toUpperCase() : ''
  const url = typeof link?.url === 'string' ? link.url.trim() : ''
  if (!ASIN.test(asin) || !/^https:\/\//i.test(url) || !isAmazonImageUrl(url)) return null
  const size = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.round(v) : null)
  return { url, width: size(link.width), height: size(link.height), asin, expiresAt }
}
